// src/pages/admin/ClientManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  getClients,
  createClient,
  updateClient,
  deleteClient,
  resetClientPassword,
  getUnassignedCountries,
  assignCountryToClient,
  getClientDataCount
} from '../api/client.service';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Key,
  Globe,
  Mail,
  Phone,
  Building,
  User,
  MapPin,
  Percent,
  Search,
  RefreshCw,
  X,
  Check,
  AlertCircle
} from 'lucide-react';

const ClientManagement = ({ user }) => {
  const [clients, setClients] = useState([]); // ✅ Always initialize as array
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [unassignedCountries, setUnassignedCountries] = useState([]);
  const [clientCounts, setClientCounts] = useState({});
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    commission_percentage: 0,
    createdby: user?.user_id || ''
  });

  const [assignData, setAssignData] = useState({
    client_id: '',
    country_id: ''
  });

  useEffect(() => {
    loadClients();
    loadUnassignedCountries();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getClients();
      console.log('Clients API response:', res); // Debug log
      
      // ✅ Safe check - ensure we always have an array
      const clientsData = res?.data || [];
      setClients(Array.isArray(clientsData) ? clientsData : []);
      
      // Load counts for each client
      if (Array.isArray(clientsData)) {
        clientsData.forEach(client => {
          if (client?.id) loadClientCounts(client.id);
        });
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients. Please try again.');
      setClients([]); // ✅ Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadClientCounts = async (clientId) => {
    if (!clientId) return;
    try {
      const res = await getClientDataCount(clientId);
      setClientCounts(prev => ({
        ...prev,
        [clientId]: res?.data || { countries_count: 0, destinations_count: 0, activities_count: 0 }
      }));
    } catch (err) {
      console.error('Error loading client counts:', err);
    }
  };

  const loadUnassignedCountries = async () => {
    try {
      const res = await getUnassignedCountries();
      setUnassignedCountries(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading unassigned countries:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (selectedClient) {
        await updateClient(selectedClient.id, {
          ...formData,
          modifyby: user?.user_id
        });
        alert('Client updated successfully!');
      } else {
        await createClient(formData);
        alert('Client created successfully!');
      }
      setShowModal(false);
      resetForm();
      loadClients();
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await deleteClient(id);
      alert('Client deleted successfully!');
      loadClients();
    } catch (err) {
      alert('Failed to delete client');
    }
  };

  const handleResetPassword = async (id) => {
    if (!window.confirm('Reset password for this client? New password will be sent to their email.')) return;
    try {
      await resetClientPassword(id);
      alert('Password reset successfully! Check client email.');
    } catch (err) {
      alert('Failed to reset password');
    }
  };

  const handleAssignCountry = async () => {
    if (!assignData.client_id || !assignData.country_id) {
      alert('Please select both client and country');
      return;
    }
    try {
      await assignCountryToClient(assignData);
      alert('Country assigned successfully!');
      setShowAssignModal(false);
      loadUnassignedCountries();
      loadClientCounts(assignData.client_id);
      setAssignData({ client_id: '', country_id: '' });
    } catch (err) {
      alert('Failed to assign country');
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      commission_percentage: 0,
      createdby: user?.user_id || ''
    });
    setSelectedClient(null);
    setError('');
  };

  const openEditModal = (client) => {
    setSelectedClient(client);
    setFormData({
      company_name: client.company_name || '',
      contact_person: client.contact_person || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      commission_percentage: client.commission_percentage || 0,
      createdby: user?.user_id || ''
    });
    setShowModal(true);
  };

  // ✅ Safe filtering - check if clients is array
  const filteredClients = Array.isArray(clients) 
    ? clients.filter(client =>
        client?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client?.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Users className="w-8 h-8 text-amber-500" />
          Client Management
        </h1>
        <p className="text-gray-500 mt-2">Manage clients, assign countries, and track commissions</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl flex items-center gap-3">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-6">
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg"
        >
          <Plus size={20} />
          Create New Client
        </button>

        <button
          onClick={() => setShowAssignModal(true)}
          className="bg-white text-gray-700 px-6 py-3 rounded-xl font-bold flex items-center gap-2 border border-gray-200 hover:border-amber-300 transition-all shadow-sm"
        >
          <Globe size={20} className="text-amber-500" />
          Assign Country
        </button>

        <button
          onClick={loadClients}
          className="bg-white text-gray-700 px-6 py-3 rounded-xl font-bold flex items-center gap-2 border border-gray-200 hover:border-amber-300 transition-all shadow-sm ml-auto"
        >
          <RefreshCw size={20} className="text-amber-500" />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-2xl shadow-sm mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search clients..."
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Clients Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <RefreshCw className="animate-spin text-amber-500" size={40} />
        </div>
      ) : filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map(client => (
            <div key={client.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-lg">{client.company_name}</h3>
                    <p className="text-amber-100 text-sm mt-1">{client.contact_person}</p>
                  </div>
                  <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                    ID: {client.id}
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail size={16} className="text-amber-500" />
                    <a href={`mailto:${client.email}`} className="text-gray-600 hover:text-amber-600">
                      {client.email}
                    </a>
                  </div>
                  {client.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone size={16} className="text-amber-500" />
                      <span className="text-gray-600">{client.phone}</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 py-3 border-t border-b border-gray-100">
                  <div className="text-center">
                    <div className="text-xl font-bold text-amber-600">
                      {clientCounts[client.id]?.countries_count || 0}
                    </div>
                    <div className="text-xs text-gray-500">Countries</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-amber-600">
                      {clientCounts[client.id]?.destinations_count || 0}
                    </div>
                    <div className="text-xs text-gray-500">Destinations</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-amber-600">
                      {clientCounts[client.id]?.activities_count || 0}
                    </div>
                    <div className="text-xs text-gray-500">Activities</div>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                  <span className="text-sm font-medium text-gray-600 flex items-center gap-1">
                    <Percent size={14} className="text-amber-500" />
                    Commission
                  </span>
                  <span className="font-bold text-green-600">{client.commission_percentage}%</span>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => openEditModal(client)}
                    className="flex-1 bg-blue-50 text-blue-600 py-2 rounded-xl font-medium flex items-center justify-center gap-1 hover:bg-blue-100 transition-colors"
                  >
                    <Edit size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleResetPassword(client.id)}
                    className="flex-1 bg-amber-50 text-amber-600 py-2 rounded-xl font-medium flex items-center justify-center gap-1 hover:bg-amber-100 transition-colors"
                  >
                    <Key size={16} />
                    Reset
                  </button>
                  <button
                    onClick={() => handleDelete(client.id)}
                    className="flex-1 bg-red-50 text-red-600 py-2 rounded-xl font-medium flex items-center justify-center gap-1 hover:bg-red-100 transition-colors"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white rounded-2xl">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium text-lg">No clients found</p>
          <p className="text-sm text-gray-400 mt-1">Click "Create New Client" to add one</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedClient ? 'Edit Client' : 'Create New Client'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-600 flex items-center gap-2">
                    <Building size={16} className="text-amber-500" />
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-600 flex items-center gap-2">
                    <User size={16} className="text-amber-500" />
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-600 flex items-center gap-2">
                    <Mail size={16} className="text-amber-500" />
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-600 flex items-center gap-2">
                    <Phone size={16} className="text-amber-500" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-gray-600 flex items-center gap-2">
                    <MapPin size={16} className="text-amber-500" />
                    Address
                  </label>
                  <textarea
                    rows="3"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-600 flex items-center gap-2">
                    <Percent size={16} className="text-amber-500" />
                    Commission %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                    value={formData.commission_percentage}
                    onChange={(e) => setFormData({...formData, commission_percentage: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-5 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-xl font-bold hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <RefreshCw className="animate-spin" size={20} /> : <Check size={20} />}
                  {selectedClient ? 'Update Client' : 'Create Client'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-100 text-gray-600 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Country Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Assign Country to Client</h2>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <select
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                value={assignData.client_id}
                onChange={(e) => setAssignData({...assignData, client_id: e.target.value})}
              >
                <option value="">Select Client</option>
                {Array.isArray(clients) && clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.company_name}
                  </option>
                ))}
              </select>

              <select
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                value={assignData.country_id}
                onChange={(e) => setAssignData({...assignData, country_id: e.target.value})}
              >
                <option value="">Select Country</option>
                {unassignedCountries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.country_name}
                  </option>
                ))}
              </select>

              <button
                onClick={handleAssignCountry}
                disabled={!assignData.client_id || !assignData.country_id}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-xl font-bold hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
              >
                Assign Country
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;